import { useState } from 'react';
import { setAgentMandato, enviarSmsMandato, verificarSmsMandato, apiError } from '../lib/agents';

/**
 * Mandato del bot pre-armado (ClientAgent): pide_permiso vs autonomo.
 * No se muestra en sesiones Salomon/MCP. Autonomo pide SMS al telefono
 * de la cuenta; no trabaja solo hasta que coincida. No mueve plata real.
 */

function flagsDe(agente) {
  const m = agente && agente.mandato;
  const tipoRaw = m && typeof m === 'object' ? m.tipo : m;
  const tipo = tipoRaw === 'autonomo' ? 'autonomo' : 'pide_permiso';
  const smsConfirmado = Boolean(
    (m && typeof m === 'object' && m.smsConfirmado) ||
    (agente && agente.smsConfirmado)
  );
  const puedeAutonomo = Boolean(agente && agente.puedeAutonomo);
  return { tipo, smsConfirmado, puedeAutonomo };
}

function textoEstado({ tipo, smsConfirmado, puedeAutonomo }) {
  if (tipo !== 'autonomo') return 'Te pide permiso antes de operar.';
  if (!smsConfirmado) return 'Pediste que trabaje solo. Falta el SMS.';
  if (!puedeAutonomo) return 'Mandato listo; el cercado o la práctica aún no lo sueltan.';
  return 'Puede operar solo dentro del cercado de práctica.';
}

export default function MandatoAgente({ agente, onChanged }) {
  const [enviando, setEnviando] = useState(null);
  const [error, setError] = useState(null);
  const [codigo, setCodigo] = useState('');

  if (!agente || !agente.id) return null;

  const flags = flagsDe(agente);
  const estado = textoEstado(flags);
  const ocupado = Boolean(enviando);
  const faltaSms = flags.tipo === 'autonomo' && !flags.smsConfirmado;

  const onTipo = async (tipo) => {
    if (ocupado || tipo === flags.tipo) return;
    setEnviando(tipo);
    setError(null);
    try {
      await setAgentMandato(agente.id, tipo);
      setCodigo('');
      if (onChanged) await onChanged();
    } catch (e) {
      setError(apiError(e, 'No pudimos cambiar el mandato. Intenta de nuevo.'));
    } finally {
      setEnviando(null);
    }
  };

  const onEnviarSms = async () => {
    if (ocupado) return;
    setEnviando('sms');
    setError(null);
    try {
      await enviarSmsMandato(agente.id);
    } catch (e) {
      setError(apiError(e, 'No pudimos enviar el código. Intenta de nuevo.'));
    } finally {
      setEnviando(null);
    }
  };

  const onConfirmarSms = async () => {
    if (ocupado) return;
    const valor = String(codigo || '').trim();
    if (!valor) {
      setError('Escribe el código que te llegó al teléfono de la cuenta.');
      return;
    }
    setEnviando('verificar');
    setError(null);
    try {
      await verificarSmsMandato(agente.id, valor);
      setCodigo('');
      if (onChanged) await onChanged();
    } catch (e) {
      setError(apiError(e, 'Ese código no coincide. Intenta de nuevo.'));
    } finally {
      setEnviando(null);
    }
  };

  return (
    <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6">
      <p className="text-xs text-gray-500">Cómo opera</p>
      <p className="mt-1.5 text-[15px] leading-snug text-gray-800">{estado}</p>
      <p className="mt-2 text-sm text-gray-600">
        {faltaSms
          ? 'Te mandamos el código al teléfono de tu cuenta. No trabaja solo hasta que coincida. No mueve plata real.'
          : 'No mueve plata real.'}
      </p>
      {error && (
        <p className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
          {error}
        </p>
      )}
      <div className="mt-4 flex gap-2 flex-wrap">
        <button
          type="button"
          disabled={ocupado}
          aria-pressed={flags.tipo === 'pide_permiso'}
          onClick={() => onTipo('pide_permiso')}
          className={`text-sm px-4 py-2 rounded-lg disabled:opacity-50 ${
            flags.tipo === 'pide_permiso'
              ? 'bg-[#2A59C6] text-white'
              : 'border border-gray-300 text-gray-700'
          }`}
        >
          {enviando === 'pide_permiso' ? 'Guardando…' : 'Que me pida permiso'}
        </button>
        <button
          type="button"
          disabled={ocupado}
          aria-pressed={flags.tipo === 'autonomo'}
          onClick={() => onTipo('autonomo')}
          className={`text-sm px-4 py-2 rounded-lg disabled:opacity-50 ${
            flags.tipo === 'autonomo'
              ? 'bg-[#2A59C6] text-white'
              : 'border border-gray-300 text-gray-700'
          }`}
        >
          {enviando === 'autonomo' ? 'Guardando…' : 'Que trabaje solo'}
        </button>
      </div>
      {faltaSms && (
        <div className="mt-4 space-y-3">
          <button
            type="button"
            disabled={ocupado}
            onClick={onEnviarSms}
            className="text-sm px-4 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50"
          >
            {enviando === 'sms' ? 'Enviando…' : 'Enviar código'}
          </button>
          <div className="flex gap-2 flex-wrap">
            <input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="Código SMS"
              inputMode="numeric"
              autoComplete="one-time-code"
              disabled={ocupado}
              className="flex-1 min-w-[120px] border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            />
            <button
              type="button"
              disabled={ocupado || !String(codigo || '').trim()}
              onClick={onConfirmarSms}
              className="text-sm px-4 py-2 rounded-lg bg-[#2A59C6] text-white disabled:opacity-50"
            >
              {enviando === 'verificar' ? 'Confirmando…' : 'Confirmar SMS'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
